import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  Copy,
  Download,
  FolderOpen,
  Mic,
  MoreHorizontal,
  Plus,
  SlidersHorizontal,
  Upload,
  X,
} from 'lucide-react';
import type { CompositionDocument } from '../composition/document';
import { DEFAULT_DRAFT_TITLE } from '../composition/snapshot';
import type { LocalSaveStatus } from '../composition/useCompositionLibrary';
import type { CloudStatus } from '../composition/sync';
import './CompositionWorkspace.css';

export function CompositionHeader({
  title,
  onTitle,
  status,
  cloudStatus,
  cloudEnabled,
  onSync,
  error,
  onRetry,
  onLibrary,
  onBack,
  onSettings,
  onExport,
  onPublish,
  onCommands,
  onTakes,
  takeCount,
  reviewCount,
  publishLabel,
  inputLabel,
  onInput,
}: {
  title: string;
  onTitle: (title: string) => void;
  status: LocalSaveStatus;
  cloudStatus: CloudStatus;
  cloudEnabled: boolean;
  onSync: () => void;
  error: string;
  onRetry: () => void;
  onLibrary: () => void;
  onBack: () => void;
  onSettings: () => void;
  onExport: () => void;
  onPublish: () => void;
  onCommands: () => void;
  onTakes: () => void;
  takeCount: number;
  /** Observations recognition was unsure about, across every take. */
  reviewCount: number;
  publishLabel: string;
  inputLabel: string;
  onInput: () => void;
}) {
  const saveLabel =
    status === 'saved'
      ? cloudStatus === 'synced'
        ? 'Synced'
        : cloudStatus === 'syncing'
          ? 'Saved · syncing…'
          : cloudStatus === 'offline'
            ? 'Saved · offline'
            : 'Saved on this device'
      : status === 'saving'
        ? 'Saving on this device…'
        : status === 'error'
          ? 'Could not save on this device'
          : 'Opening sketches…';
  return (
    <>
      <header className="composition-header">
        <Tooltip title="Repertoire">
          <IconButton aria-label="Back to repertoire" onClick={onBack}>
            <ArrowLeft size={20} />
          </IconButton>
        </Tooltip>
        <div className="composition-heading">
          <div className="composition-eyebrow">COMPOSE</div>
          <input
            className="composition-title"
            aria-label="Composition title"
            value={title}
            maxLength={160}
            onChange={(event) => onTitle(event.target.value)}
            onBlur={() => {
              if (!title.trim()) onTitle(DEFAULT_DRAFT_TITLE);
            }}
          />
        </div>
        <div
          className={`composition-save-state ${status === 'error' ? 'has-error' : ''}`}
          role="status"
          data-composition-save-state
        >
          {status === 'saved' && <Check size={14} />}
          <span>{saveLabel}</span>
        </div>
        <div className="composition-header-actions">
          <Button startIcon={<FolderOpen size={17} />} onClick={onLibrary}>
            Sketches
          </Button>
          <Button className="composition-input-status" onClick={onInput}>
            <span className="composition-input-dot" />
            {inputLabel}
          </Button>
          <Tooltip title="Export and backup">
            <IconButton aria-label="Export and backup" onClick={onExport}>
              <Download size={19} />
            </IconButton>
          </Tooltip>
          <Button
            className="composition-takes"
            data-composition-takes
            startIcon={<Mic size={17} />}
            onClick={onTakes}
          >
            Takes{takeCount > 0 ? ` · ${takeCount}` : ''}
            {reviewCount > 0 && (
              <span className="composition-take-flag" data-composition-review-count>
                <AlertTriangle size={13} />
                {reviewCount}
              </span>
            )}
          </Button>
          <Tooltip title={publishLabel}>
            <Button className="composition-publish" data-composition-publish onClick={onPublish}>
              Add to repertoire
            </Button>
          </Tooltip>
          <Tooltip title="Composition settings">
            <IconButton aria-label="Composition settings" onClick={onSettings}>
              <SlidersHorizontal size={19} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Commands and shortcuts">
            <IconButton aria-label="Commands and shortcuts" onClick={onCommands}>
              <MoreHorizontal size={21} />
            </IconButton>
          </Tooltip>
        </div>
      </header>
      {error && (
        <div className="composition-error" role="alert">
          {error}
          {status === 'error' && <Button onClick={onRetry}>Retry save</Button>}
          {cloudStatus === 'error' && <Button onClick={onSync}>Retry sync</Button>}
        </div>
      )}
      {cloudEnabled && cloudStatus === 'offline' && (
        <div className="composition-offline" role="status">
          Edits are saved here and will sync when you reconnect.
        </div>
      )}
    </>
  );
}

export function SketchLibrary({
  open,
  documents,
  cloudEnabled,
  activeId,
  onClose,
  onOpen,
  onNew,
  onImport,
  onBackup,
  onMusicXml,
  onHistory,
  onRecover,
}: {
  open: boolean;
  documents: CompositionDocument[];
  cloudEnabled: boolean;
  activeId?: string;
  onClose: () => void;
  onOpen: (document: CompositionDocument) => void;
  onNew: () => void;
  onImport: (file: File) => void;
  onBackup: () => void;
  onMusicXml: () => void;
  onHistory: () => Promise<CompositionDocument[]>;
  onRecover: (document: CompositionDocument) => void;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [history, setHistory] = useState<CompositionDocument[] | null>(null);
  const [historyError, setHistoryError] = useState('');
  useEffect(() => {
    setHistory(null);
    setHistoryError('');
  }, [open, activeId]);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: 'composition-dialog' }}
    >
      <DialogTitle className="composition-dialog-heading">
        Your sketches
        <IconButton aria-label="Close sketches" onClick={onClose}>
          <X size={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <p className="composition-muted">
          {cloudEnabled
            ? 'Sketches save on this device first, then sync to your account. '
            : 'Sketches are saved on this device. '}
          A composition backup keeps your editable music and captured MIDI performance together.
        </p>
        <div className="composition-library-actions">
          <Button variant="contained" startIcon={<Plus size={18} />} onClick={onNew}>
            New composition
          </Button>
          <Button startIcon={<Upload size={17} />} onClick={() => fileInput.current?.click()}>
            Import backup
          </Button>
          <input
            ref={fileInput}
            type="file"
            accept=".museflow,.json,application/json"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) onImport(file);
              event.target.value = '';
            }}
          />
        </div>
        <div className="composition-sketch-list">
          {documents.map((document) => (
            <button
              key={document.id}
              className="composition-sketch"
              onClick={() => onOpen(document)}
              aria-current={document.id === activeId ? 'true' : undefined}
            >
              <span className="composition-sketch-icon">
                <Copy size={21} />
              </span>
              <span>
                <strong>{document.title}</strong>
                <small>
                  {new Date(document.updatedAt).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                  })}{' '}
                  · {document.score.draftMeasureCount} bars · {document.takes.length} takes
                  {' · '}
                  {document.cloud?.syncedLocalRevision === document.revision
                    ? 'Synced'
                    : 'On this device'}
                </small>
              </span>
              {document.id === activeId && <span className="composition-current">Open</span>}
            </button>
          ))}
        </div>
        <div className="composition-library-actions composition-library-export">
          <Button startIcon={<Download size={17} />} onClick={onBackup}>
            Composition backup
          </Button>
          <Button onClick={onMusicXml}>Export MusicXML</Button>
          <Button
            onClick={() => {
              void onHistory()
                .then(setHistory)
                .catch((reason) =>
                  setHistoryError(
                    reason instanceof Error
                      ? reason.message
                      : 'Recovery history could not be read.',
                  ),
                );
            }}
          >
            Recent versions
          </Button>
        </div>
        {historyError && <p role="alert">{historyError}</p>}
        {history && (
          <div data-composition-history>
            <p className="composition-muted">
              Open a recent version as a separate sketch. Your current composition stays available.
            </p>
            {history.slice(0, 6).map((version) => (
              <Button
                key={version.revision}
                fullWidth
                onClick={() => onRecover(version)}
                sx={{ justifyContent: 'space-between', minHeight: 44 }}
              >
                <span>Recover version {version.revision}</span>
                <span>
                  {new Date(version.updatedAt).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}{' '}
                  · {version.score.capturedEvents.length} events
                </span>
              </Button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function PassageControls({
  measureCount,
  fromBar,
  toBar,
  onRange,
  onSelect,
  onLoop,
  looping,
  selectionLabel,
  actions,
  drawing,
  onDrawing,
  zoom,
  onZoom,
  pasteModes,
  pasteMode,
  onPasteMode,
  loopLabel,
  stepEntry,
  stepEntryAvailable,
  onStepEntry,
  onStepRest,
  pianoRoll,
  onPianoRoll,
  busy,
}: {
  measureCount: number;
  fromBar: number;
  toBar: number;
  onRange: (from: number, to: number) => void;
  onSelect: () => void;
  onLoop: () => void;
  looping: boolean;
  selectionLabel: string;
  actions: { id: string; label: string; enabled: boolean; execute: () => void }[];
  drawing: boolean;
  onDrawing: (value: boolean) => void;
  zoom: number;
  onZoom: (value: number) => void;
  /** Insert, Replace and Merge move whole passages; Pitches and Rhythm move one aspect. */
  pasteModes: { id: string; label: string; hint: string }[];
  pasteMode: string;
  onPasteMode: (mode: string) => void;
  loopLabel?: string;
  stepEntry: boolean;
  /** Step entry needs a connected MIDI instrument to play the notes. */
  stepEntryAvailable: boolean;
  onStepEntry: (value: boolean) => void;
  onStepRest: () => void;
  pianoRoll: boolean;
  onPianoRoll: (value: boolean) => void;
  /** True while recording, when tools that change the score are held off. */
  busy: boolean;
}) {
  return (
    <Box className="composition-tools" data-composition-tools>
      <div className="composition-tool-row">
        <div className="composition-tool-toggle" role="group" aria-label="Score tool">
          <Button aria-pressed={!drawing} onClick={() => onDrawing(false)}>
            Select
          </Button>
          <Button aria-pressed={drawing} onClick={() => onDrawing(true)}>
            Draw
          </Button>
        </div>
        <div className="composition-bar-controls">
          <span>Bars</span>
          <TextField
            type="number"
            size="small"
            value={fromBar}
            inputProps={{ 'aria-label': 'First bar', min: 1, max: measureCount }}
            onChange={(event) =>
              onRange(Math.max(1, Math.min(measureCount, Number(event.target.value) || 1)), toBar)
            }
          />
          <span>–</span>
          <TextField
            type="number"
            size="small"
            value={toBar}
            inputProps={{ 'aria-label': 'Last bar', min: fromBar, max: measureCount }}
            onChange={(event) =>
              onRange(
                fromBar,
                Math.max(fromBar, Math.min(measureCount, Number(event.target.value) || fromBar)),
              )
            }
          />
          <Button onClick={onSelect}>Select bars</Button>
          <Button aria-pressed={looping} onClick={onLoop}>
            {looping ? 'Loop on' : 'Loop bars'}
          </Button>
        </div>
        <div className="composition-mode-group" role="group" aria-label="Views">
          <Button
            aria-pressed={pianoRoll}
            data-composition-piano-roll-toggle
            onClick={() => onPianoRoll(!pianoRoll)}
          >
            {pianoRoll ? 'Roll on' : 'Piano roll'}
          </Button>
          <Tooltip
            title={
              stepEntryAvailable
                ? 'Play a note or chord on the keyboard to write it at the cursor with the chosen value'
                : 'Connect a MIDI keyboard to enter notes by step'
            }
          >
            <span>
              <Button
                aria-pressed={stepEntry}
                disabled={!stepEntryAvailable || busy}
                data-composition-step-entry-toggle
                onClick={() => onStepEntry(!stepEntry)}
              >
                {stepEntry ? 'Step entry on' : 'Step entry'}
              </Button>
            </span>
          </Tooltip>
          {stepEntry && (
            <Button data-composition-step-rest disabled={busy} onClick={onStepRest}>
              Rest
            </Button>
          )}
        </div>
        <div className="composition-zoom">
          <IconButton
            aria-label="Zoom out"
            disabled={zoom <= 0.75}
            onClick={() => onZoom(Math.max(0.75, zoom - 0.1))}
          >
            −
          </IconButton>
          <span>{Math.round(zoom * 100)}%</span>
          <IconButton
            aria-label="Zoom in"
            disabled={zoom >= 1.5}
            onClick={() => onZoom(Math.min(1.5, zoom + 0.1))}
          >
            +
          </IconButton>
        </div>
      </div>
      <div className="composition-mode-row" data-composition-paste-modes>
        <div className="composition-mode-group" role="group" aria-label="Paste mode">
          <span>Paste:</span>
          {pasteModes.map((mode) => (
            <Tooltip key={mode.id} title={mode.hint}>
              <Button
                disabled={busy}
                aria-pressed={pasteMode === mode.id}
                data-composition-paste-mode={mode.id}
                onClick={() => onPasteMode(mode.id)}
              >
                {mode.label}
              </Button>
            </Tooltip>
          ))}
        </div>
        {loopLabel && <span data-composition-loop>{loopLabel}</span>}
        {stepEntry && (
          <span className="composition-step-hint" data-composition-step-hint>
            Step entry · play to write at the cursor
          </span>
        )}
      </div>
      <div className="composition-selection-row">
        <span className="composition-selection-label" role="status">
          {selectionLabel}
        </span>
        <div
          className="composition-selection-actions"
          role="toolbar"
          aria-label="Selection actions"
        >
          {actions.map((action) => (
            <Button key={action.id} disabled={!action.enabled} onClick={action.execute}>
              {action.label}
            </Button>
          ))}
        </div>
      </div>
    </Box>
  );
}
